import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import * as Sentry from '@sentry/react';
import './i18n';
import { ChakraProvider } from '@chakra-ui/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import { system } from './styles/theme';
import { AuthContextProvider } from './contexts/AuthContext';
import { ColorModeProvider } from './components/ui/color-mode';
import Auth from './pages/Auth';
import Menus from './pages/Menus';
import MenuDetail from './pages/MenuDetail';
import MenuView from './pages/MenuView';
import BusinessSettings from './pages/BusinessSettings';
import Profile from './pages/Profile';
import RootRedirect from './pages/RootRedirect';
import AppleCallback from './pages/AppleCallback';
import AdminStockImages from './pages/AdminStockImages';
import ProtectedRoute from './components/ProtectedRoute';
import { PostHogProvider, PostHogErrorBoundary } from '@posthog/react';

const isProduction = import.meta.env.VITE_APP_ENV === 'production';

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
  autocapture: isProduction,
  capture_pageview: isProduction,
  capture_pageleave: isProduction,
  disable_session_recording: !isProduction,
} as const

Sentry.init({
  dsn: 'https://70b3ffaeaf44de46d08a8a27200d48f6@o4510948770250752.ingest.us.sentry.io/4510948790435840',
  enabled: isProduction,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api(-staging)?\.tryloyaltycards\.com/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    const message = event.exception?.values?.[0]?.value ?? '';
    if (message.includes('User rejected the request')) return null;
    return event;
  },
});

const queryClient = new QueryClient();

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const toastStyle = {
  borderRadius: 8,
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 8,
  paddingBottom: 8,
  minHeight: 20,
  width: 'fit-content',
  marginLeft: 'auto',
  marginRight: 'auto',
  fontSize: 14,
  fontWeight: 500,
  color: 'white',
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
      <PostHogErrorBoundary>
    <ChakraProvider value={system}>
      <ColorModeProvider>
        <GoogleOAuthProvider clientId={googleClientId}>
          <QueryClientProvider client={queryClient}>
            <AuthContextProvider>
              <BrowserRouter>
              <Routes>
                {/* Root redirects based on auth status */}
                <Route path="/" element={<RootRedirect />} />

                {/* Auth routes */}
                <Route path="/login" element={<Auth />} />
                <Route path="/signup" element={<Auth />} />
                <Route path="/reset-password" element={<Auth />} />
                <Route path="/verify-email" element={<Auth />} />
                <Route path="/auth/apple/callback" element={<AppleCallback />} />

                {/* Redirect /businesses to root (auto-resolves business) */}
                <Route path="/businesses" element={<Navigate to="/" replace />} />

                {/* Business-scoped routes */}
                <Route
                  path="/businesses/:businessId/menus"
                  element={
                    <ProtectedRoute>
                      <Menus />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/businesses/:businessId/menus/:menuId"
                  element={
                    <ProtectedRoute>
                      <MenuDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/businesses/:businessId/settings"
                  element={
                    <ProtectedRoute>
                      <BusinessSettings />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin/stock-images"
                  element={
                    <ProtectedRoute>
                      <AdminStockImages />
                    </ProtectedRoute>
                  }
                />

                {/* Profile route */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />

                {/* Public menu view */}
                <Route path="/m/:slug" element={<MenuView />} />
                <Route path="/m/:slug/duplicate" element={<MenuView showDuplicate />} />

                {/* Redirect any unknown routes to root */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </BrowserRouter>
              <ToastContainer
                theme="dark"
                autoClose={2500}
                position="top-center"
                pauseOnHover={false}
                closeOnClick
                hideProgressBar
                draggable={false}
                closeButton={false}
                transition={Slide}
                toastStyle={toastStyle}
              />
            </AuthContextProvider>
          </QueryClientProvider>
        </GoogleOAuthProvider>
      </ColorModeProvider>
    </ChakraProvider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  </StrictMode>
);
